/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BackgroundContainer, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { css, cx } from "@panda/css";
import { TabbedContentImageItemFragment, TabbedContentImageSectionFragment } from "@cosmos/hygraph-schema";
import { useMemo } from "react";
import { CosmosTabs as Tabs } from "@components/elements";
import { TabbedContentImageContent } from "./TabbedContentImage.Content";
import { VStack } from "@panda/jsx";
import { SectionHeading } from "@components/elements/molecules/section-heading";

const cardStyles = css({
	w: "full",
	backdropFilter: "blur(50px)",
	borderRadius: "eds.borderRadius.32",
	background: "eds.palette.transparent.light.005",
	border: "1px solid",
	borderColor: "eds.palette.transparent.light.005",
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.40",
	padding: "eds.dimension.64",
	mdDown: {
		padding: "eds.dimension.48",
	},
	smDown: {
		paddingInline: "eds.dimension.24",
	},
});

const analyticsEventValue = {
	component: "TabbedContentImage",
	section: "section",
};

const buttonListStyle = css({
	"& .cosmos-button": { paddingInline: "eds.dimension.16" },
});

export const TabbedContentImageSection = (props: TabbedContentImageSectionFragment): React.JSX.Element | null => {
	const config = props.config || {};
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = useMemo(
		() => Boolean(tabs?.length && tabs.some((tab) => tab?.asset || tab?.title?.text || tab?.desc)),
		[tabs],
	);
	if (!validHeading && !validContent) return null;

	const enableTabs = tabs.length > 1;

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<VStack gap="eds.dimension.56" alignItems="flex-start" className="tabbed-content-image">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<Tabs>
								<div className={cx("card", cardStyles)}>
									{enableTabs ? (
										<Tabs.ButtonList className={buttonListStyle}>
											{tabs.map((tab: TabbedContentImageItemFragment, index: number) => (
												<Tabs.Button
													key={tab.tabLabel || index}
													index={index}
													label={tab.tabLabel ?? ""}
													analytics={{
														eventCategory: "button",
														interactionType: "click",
														eventAction: "select",
														eventLabel: tab.tabLabel ?? null,
														eventValue: analyticsEventValue,
													}}
												/>
											))}
										</Tabs.ButtonList>
									) : null}
									{tabs.map((tab: TabbedContentImageItemFragment, index: number) => (
										<Tabs.Panel key={index} index={index}>
											<TabbedContentImageContent singleTab={!enableTabs} {...tab} />
										</Tabs.Panel>
									))}
								</div>
							</Tabs>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
